import jsCookie from 'js-cookie';
import { useTranslation } from 'next-i18next';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';

import CropsDropdown from '@/modules/periodicJobs/components/CropsDropdown';
import { PeriodicJobsContext } from '@/modules/periodicJobs/context/context';
import { periodicJobsActionsTypes as ActionsTypes } from '@/modules/periodicJobs/context/reducer';
import { IPeriodicJobs } from '@/modules/periodicJobs/types';
import { useGetAllCrops } from '@/modules/settings/shared/apiHooks';
import { NextImage, Typography } from '@/shared/components/atoms';

import Arrow from '~/images/shared/arrowDown.svg';

interface HeaderProps {
  entityId?: number;
}

const Header: React.FC<HeaderProps> = ({ entityId }) => {
  const [isOpen, setIsOpen] = useState(false);

  const { t } = useTranslation();

  const {
    state: { selectedCrop },
    dispatch,
  } = useContext(PeriodicJobsContext);

  const { data } = useGetAllCrops(entityId);

  const cropsRef = useRef<HTMLDivElement>(null);

  const closeDropdownHandler = () => {
    setIsOpen(false);
  };

  const toggleDropdownHandler = () => {
    setIsOpen((prevState) => !prevState);
  };

  const setCropState = useCallback(
    (crop: IPeriodicJobs.CropSchema) => {
      dispatch({ type: ActionsTypes.setCrop, payload: crop });
    },
    [dispatch]
  );

  const persistSelectedCrop = useCallback(() => {
    if (data && data.list && data.list.length > 0) {
      const savedCrop = jsCookie.get('periodicJobsCrop');

      if (!savedCrop) {
        setCropState(data.list[0]);
        return;
      }

      const crop = JSON.parse(savedCrop) as IPeriodicJobs.CropSchema;
      const cropFromApi = data.list.find((c) => c.id === crop.id);

      if (cropFromApi?.name !== crop.name) {
        setCropState(data.list[0]);
        return;
      }

      setCropState(crop);
    }
  }, [data, setCropState]);

  const closeOpenMenus = useCallback(
    (e: MouseEvent) => {
      if (
        isOpen &&
        cropsRef.current &&
        !cropsRef.current.contains(e.target as Node)
      ) {
        closeDropdownHandler();
      }
    },
    [cropsRef, isOpen]
  );

  useEffect(() => {
    document.addEventListener('mousedown', closeOpenMenus);

    return () => {
      document.removeEventListener('mousedown', closeOpenMenus);
    };
  }, [cropsRef, closeOpenMenus]);

  useEffect(() => {
    persistSelectedCrop();
  }, [persistSelectedCrop]);

  return (
    <div ref={cropsRef} className='relative mb-10 select-none'>
      <div
        onClick={toggleDropdownHandler}
        className='flex cursor-pointer flex-row items-center justify-start gap-x-6'
      >
        <Typography
          variant='h2'
          component='h2'
          weight='bold'
          className='text-black'
        >
          {t('crop')} : {selectedCrop?.name ?? 'Not Found'}
        </Typography>
        {selectedCrop?.name && selectedCrop?.image && (
          <div className='h-10 w-48'>
            <NextImage
              width={100}
              height={100}
              src={selectedCrop?.image}
              alt={selectedCrop?.name}
              className='max-h-full max-w-full'
              useSkeleton
            />
          </div>
        )}
        <Arrow />
      </div>
      {isOpen && (
        <CropsDropdown crops={data?.list} onClose={closeDropdownHandler} />
      )}
    </div>
  );
};

export default Header;
